/**
 * Bulk Order Form JavaScript - Redesigned
 * Professional B2B Wholesale Ordering Interface
 * ================================================
 */

(function() {
  'use strict';

  // Namespace to avoid conflicts
  const BulkOrderForm = {
    config: {
      selectors: {
        section: '[data-section-type="bulk-order"]',
        controls: '[data-sticky-controls]',
        productRow: '[data-product-row]',
        productsList: '[data-products-list]',
        qtyInput: '[data-qty-input]',
        qtyButton: '[data-qty-change]',
        submitButton: '[data-bulk-order-submit]',
        filterButton: '[data-collection-filter]',
        filterToggle: '[data-filters-toggle]',
        filterContent: '[data-filters-content]',
        filterCount: '[data-filter-count]',
        actionbar: '[data-actionbar]',
        selectedToggle: '[data-selected-toggle]',
        selectedPanel: '[data-selected-panel]',
        quoteDialog: '[data-quote-dialog]',
        quoteSend: '[data-quote-send]',
        quoteCancel: '[data-quote-cancel]',
        resumeDialog: '[data-resume-dialog]',
        resumeBody: '[data-resume-body]',
        resumeContinue: '[data-resume-continue]',
        resumeFresh: '[data-resume-fresh]',
        contactForm: '[data-guest-contact-form]',
        contactField: '[data-contact-field]',
        messages: '[data-bulk-order-messages]',
        summaryTotal: '[data-summary-total]',
        totalItems: '[data-total-items]',
        totalPieces: '[data-total-pieces]',
        selectedItemsList: '[data-selected-items-list]',
        productSearch: '[data-product-search]',
        collectionSearch: '[data-collection-search]',
        bulkSelectAll: '[data-bulk-select-all]',
        clearAll: '[data-clear-all]',
        clearDialog: '[data-clear-dialog]',
        clearConfirm: '[data-clear-confirm]',
        clearCancel: '[data-clear-cancel]',
        exportOrder: '[data-export-order]',
        saveDraft: '[data-save-draft]',
        lineSubtotal: '[data-line-subtotal]',
        emptyState: '[data-empty-state]',
        emptyOtherPages: '[data-empty-other-pages]',
        emptySearchStore: '[data-empty-search-store]',
        resetFilters: '[data-reset-filters]',
        reorderOpen: '[data-reorder-open]',
        reorderDialog: '[data-reorder-dialog]',
        catalogResults: '[data-catalog-results]'
      },
      searchDebounceDelay: 300 // milliseconds
    },

    state: {
      isCustomerLoggedIn: false,
      currentFilter: 'all',
      selectedItems: [],
      totalAmount: 0,
      totalItems: 0,
      totalPieces: 0,
      storageKey: 'bulk_order_quantities',
      draftKey: 'bulk_order_draft',
      moneyFormat: '${{amount}}',
      strings: {},
      filtersExpanded: false,
      searchTimeout: null,
      collectionSearchTimeout: null,
      autosaveTimeout: null,
      orderCompleted: false,
      // True once the saved draft is the "live" order (restored on load or
      // written this session); gates cross-page merging in save/getSelected.
      draftRestored: false
    },

    /**
     * Initialize the bulk order form
     */
    init: function() {
      const section = document.querySelector(this.config.selectors.section);
      if (!section) return;

      // Check if customer is logged in
      const customerData = section.getAttribute('data-customer-logged-in');
      this.state.isCustomerLoggedIn = customerData && customerData !== 'false' && customerData !== 'null';

      // Get money format from section
      const moneyFormat = section.getAttribute('data-money-format');
      if (moneyFormat) {
        this.state.moneyFormat = moneyFormat;
      }

      // Load localized strings (rendered by the section via Shopify's | t)
      this.loadStrings(section);

      // All sticky offsets are constant CSS calcs anchored at the viewport
      // top (Broadcast auto-hides its header on scroll-down), so no JS
      // measuring or header-state tracking is needed.
      this.initStickyObserver();

      this.bindEvents();
      this.initializeQuantities();
      this.maybeOfferResume();
      this.updateSidebar();
      this.bindPaginationEvents();
      this.bindReorder();
    },

    /**
     * Parse the localized strings JSON emitted by the section.
     * Falls back to English defaults if the script is missing.
     */
    loadStrings: function(section) {
      const fallback = {
        selected_n: 'Selected __n__ product(s)',
        confirm_clear: 'Are you sure you want to clear all quantities?',
        all_cleared: 'All quantities cleared',
        no_items_export: 'No items selected to export',
        exported: 'Order exported to CSV',
        no_items_save: 'No items to save',
        draft_saved_n: 'Draft saved with __n__ item(s)',
        could_not_save: 'Could not save draft',
        select_one: 'Please select at least one product with a quantity greater than 0.',
        added_n: 'Successfully added __n__ item(s) to your cart!',
        cart_error: 'There was an error adding items to your cart. Please try again.',
        fill_contact: 'Please fill out all required contact fields correctly.',
        quote_sent: 'Your quote request has been sent successfully! We will contact you shortly.',
        quote_error: 'There was an error sending your quote request. Please try again or contact us directly.',
        field_required: 'This field is required',
        invalid_email: 'Please enter a valid email address',
        invalid_phone: 'Please enter a valid phone number',
        more_n: '+ __n__ more item(s)',
        resume_n: 'You have an unfinished order with __n__ item(s). Continue where you left off?',
        pcs: 'pcs'
      };

      let parsed = {};
      try {
        const el = (section || document).querySelector('[data-bulk-order-strings]');
        if (el && el.textContent.trim()) {
          parsed = JSON.parse(el.textContent);
        }
      } catch (e) {
        console.warn('Bulk order: could not parse localized strings, using defaults.', e);
      }

      this.state.strings = Object.assign({}, fallback, parsed);
    },

    /**
     * Look up a localized string, substituting the count placeholder.
     */
    t: function(key, count) {
      let str = this.state.strings[key] || key;
      if (count !== undefined && count !== null) {
        str = str.replace(/__n__/g, count);
      }
      return str;
    },

    /**
     * Flip `.is-stuck` on the section while the command bar is pinned, so the
     * bars can animate their elevation in/out. A passive scroll listener with
     * a single rect read per event; the class only toggles on state change.
     * (IntersectionObserver proved unreliable alongside this theme's scripts.)
     */
    initStickyObserver: function() {
      const section = document.querySelector(this.config.selectors.section);
      const commandbar = document.querySelector('[data-commandbar]');
      if (!section || !commandbar) return;

      let wasStuck = false;

      const check = () => {
        // The bar sticks at top: 0, so it's "stuck" once its rect reaches it
        const stuck = window.scrollY > 0 && commandbar.getBoundingClientRect().top <= 0;
        if (stuck !== wasStuck) {
          wasStuck = stuck;
          section.classList.toggle('is-stuck', stuck);
          // Body-level hook: while stuck, CSS keeps the theme's fixed header
          // out of the way so search + table header stay visible on scroll-up.
          document.body.classList.toggle('bulk-order-bars-stuck', stuck);
        }
      };

      window.addEventListener('scroll', check, { passive: true });
      check();
    },

    /**
     * Bind all event listeners
     */
    bindEvents: function() {
      // Product search with debouncing
      const productSearch = document.querySelector(this.config.selectors.productSearch);
      if (productSearch) {
        productSearch.addEventListener('input', (e) => this.debouncedProductSearch(e));
        // Also add keyup for immediate feedback on Enter key
        productSearch.addEventListener('keyup', (e) => {
          if (e.key === 'Enter') {
            clearTimeout(this.state.searchTimeout);
            this.handleProductSearch(e);
          }
        });
      }

      // Collection search within filters with debouncing
      const collectionSearch = document.querySelector(this.config.selectors.collectionSearch);
      if (collectionSearch) {
        collectionSearch.addEventListener('input', (e) => this.debouncedCollectionSearch(e));
      }

      // Filter popover: toggle on the trigger, close on outside click or Esc
      const filterToggle = document.querySelector(this.config.selectors.filterToggle);
      if (filterToggle) {
        filterToggle.addEventListener('click', () => this.toggleFilters());

        document.addEventListener('click', (e) => {
          if (!this.state.filtersExpanded) return;
          const filterWrap = filterToggle.closest('[data-bulk-order-filters]');
          if (filterWrap && !filterWrap.contains(e.target)) {
            this.closeFilters();
          }
        });

        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.state.filtersExpanded) {
            this.closeFilters();
            filterToggle.focus();
          }
        });
      }

      // Collection filter buttons
      document.querySelectorAll(this.config.selectors.filterButton).forEach(button => {
        button.addEventListener('click', (e) => this.handleFilterClick(e));
      });

      // Toolbar actions
      const bulkSelectAll = document.querySelector(this.config.selectors.bulkSelectAll);
      if (bulkSelectAll) {
        bulkSelectAll.addEventListener('click', () => this.bulkSelectAll());
      }

      const clearAll = document.querySelector(this.config.selectors.clearAll);
      if (clearAll) {
        clearAll.addEventListener('click', () => this.clearAll());
      }

      // Clear-all confirmation dialog
      const clearDialog = document.querySelector(this.config.selectors.clearDialog);
      if (clearDialog) {
        const clearConfirm = clearDialog.querySelector(this.config.selectors.clearConfirm);
        const clearCancel = clearDialog.querySelector(this.config.selectors.clearCancel);

        if (clearConfirm) {
          clearConfirm.addEventListener('click', () => {
            clearDialog.close();
            this.performClearAll();
          });
        }
        if (clearCancel) {
          clearCancel.addEventListener('click', () => clearDialog.close());
        }
        // Click on the backdrop (outside the body) dismisses without clearing.
        clearDialog.addEventListener('click', (e) => {
          if (e.target === clearDialog) clearDialog.close();
        });
      }

      // Action bar: expandable selected-items panel
      const selectedToggle = document.querySelector(this.config.selectors.selectedToggle);
      const selectedPanel = document.querySelector(this.config.selectors.selectedPanel);
      if (selectedToggle && selectedPanel) {
        selectedToggle.addEventListener('click', () => {
          const isOpen = !selectedPanel.hidden;
          selectedPanel.hidden = isOpen;
          selectedToggle.setAttribute('aria-expanded', String(!isOpen));
        });
      }

      // Resume dialog: continue restores the saved order, fresh discards it.
      // Esc / backdrop-click just closes — the saved order stays for later.
      const resumeDialog = document.querySelector(this.config.selectors.resumeDialog);
      if (resumeDialog) {
        const resumeContinue = resumeDialog.querySelector(this.config.selectors.resumeContinue);
        const resumeFresh = resumeDialog.querySelector(this.config.selectors.resumeFresh);

        if (resumeContinue) {
          resumeContinue.addEventListener('click', () => {
            resumeDialog.close();
            this.restoreQuantities();
          });
        }
        if (resumeFresh) {
          resumeFresh.addEventListener('click', () => {
            resumeDialog.close();
            this.clearSavedQuantities();
          });
        }
        resumeDialog.addEventListener('click', (e) => {
          if (e.target === resumeDialog) resumeDialog.close();
        });
      }

      // Guest quote dialog
      const quoteDialog = document.querySelector(this.config.selectors.quoteDialog);
      if (quoteDialog) {
        const quoteSend = quoteDialog.querySelector(this.config.selectors.quoteSend);
        const quoteCancel = quoteDialog.querySelector(this.config.selectors.quoteCancel);

        if (quoteSend) {
          quoteSend.addEventListener('click', () => this.handleQuoteSend(quoteSend));
        }
        if (quoteCancel) {
          quoteCancel.addEventListener('click', () => quoteDialog.close());
        }
        quoteDialog.addEventListener('click', (e) => {
          if (e.target === quoteDialog) quoteDialog.close();
        });
      }

      const exportOrder = document.querySelector(this.config.selectors.exportOrder);
      if (exportOrder) {
        exportOrder.addEventListener('click', () => this.exportToCSV());
      }

      const saveDraft = document.querySelector(this.config.selectors.saveDraft);
      if (saveDraft) {
        saveDraft.addEventListener('click', () => this.saveDraft());
      }

      const resetFilters = document.querySelector(this.config.selectors.resetFilters);
      if (resetFilters) {
        resetFilters.addEventListener('click', () => this.resetFilters());
      }

      // Quantity buttons (+ and -)
      document.querySelectorAll(this.config.selectors.qtyButton).forEach(button => {
        button.addEventListener('click', (e) => this.handleQuantityButtonClick(e));
      });

      // Quantity inputs (direct input + spreadsheet-style row navigation)
      document.querySelectorAll(this.config.selectors.qtyInput).forEach(input => {
        input.addEventListener('input', (e) => this.handleQuantityInput(e));
        input.addEventListener('blur', (e) => this.validateQuantityInput(e));
        input.addEventListener('keydown', (e) => this.handleQtyKeydown(e));
      });

      // Submit button
      const submitButton = document.querySelector(this.config.selectors.submitButton);
      if (submitButton) {
        submitButton.addEventListener('click', (e) => this.handleSubmit(e));
      }

      // Contact form validation
      if (!this.state.isCustomerLoggedIn) {
        const contactFields = document.querySelectorAll(this.config.selectors.contactField);
        contactFields.forEach(field => {
          field.addEventListener('blur', (e) => this.validateContactField(e));
        });
      }
    },

    /**
     * Initialize all quantity inputs to 0
     */
    initializeQuantities: function() {
      document.querySelectorAll(this.config.selectors.qtyInput).forEach(input => {
        if (!input.value || input.value === '') {
          input.value = '0';
        }
      });
    },

    /**
     * Debounced product search
     */
    debouncedProductSearch: function(event) {
      // Clear existing timeout
      if (this.state.searchTimeout) {
        clearTimeout(this.state.searchTimeout);
      }

      // Set new timeout
      this.state.searchTimeout = setTimeout(() => {
        this.handleProductSearch(event);
      }, this.config.searchDebounceDelay);
    },

    /**
     * Handle product search
     */
    handleProductSearch: function(event) {
      const searchTerm = event.target.value.toLowerCase().trim();
      const productRows = document.querySelectorAll(this.config.selectors.productRow);

      let visibleCount = 0;

      productRows.forEach(row => {
        // Get product data
        const productTitle = row.querySelector('.bulk-order__product-title')?.textContent.toLowerCase() || '';
        const variantTitle = row.querySelector('.bulk-order__variant-title')?.textContent.toLowerCase() || '';
        const skuEl = row.querySelector('.bulk-order__variant-sku');
        const sku = (skuEl?.dataset.sku || skuEl?.textContent || '').toLowerCase();
        const barcode = row.querySelector('.bulk-order__barcode')?.textContent.toLowerCase() || '';

        // Check if matches search
        const matchesSearch = searchTerm === '' ||
          productTitle.includes(searchTerm) ||
          variantTitle.includes(searchTerm) ||
          sku.includes(searchTerm) ||
          barcode.includes(searchTerm);

        // Check if matches current filter
        const currentFilter = this.state.currentFilter;
        let matchesFilter = true;

        if (currentFilter !== 'all') {
          const productCollections = row.getAttribute('data-collections');
          const collectionsArray = productCollections ? productCollections.split(',') : [];
          matchesFilter = collectionsArray.includes(currentFilter);
        }

        // Show/hide based on both search and filter
        if (matchesSearch && matchesFilter) {
          row.style.display = '';
          visibleCount++;
        } else {
          row.style.display = 'none';
        }
      });

      this.updateVisibleProductCount(visibleCount, this.state.currentFilter);
      this.updateCatalogResults(searchTerm);
    },

    /**
     * Debounced collection search
     */
    debouncedCollectionSearch: function(event) {
      // Clear existing timeout
      if (this.state.collectionSearchTimeout) {
        clearTimeout(this.state.collectionSearchTimeout);
      }

      // Set new timeout
      this.state.collectionSearchTimeout = setTimeout(() => {
        this.handleCollectionSearch(event);
      }, this.config.searchDebounceDelay);
    },

    /**
     * Handle collection search within filters
     */
    handleCollectionSearch: function(event) {
      const searchTerm = event.target.value.toLowerCase().trim();
      const filterItems = document.querySelectorAll('.filter-list__item:not([data-collection-filter="all"])');

      filterItems.forEach(item => {
        const collectionName = item.getAttribute('data-collection-title')?.toLowerCase() || '';
        if (searchTerm === '' || collectionName.includes(searchTerm)) {
          item.style.display = '';
        } else {
          item.style.display = 'none';
        }
      });
    },

    /**
     * Toggle the filter popover (opens over the table, never pushes it down)
     */
    toggleFilters: function() {
      if (this.state.filtersExpanded) {
        this.closeFilters();
      } else {
        this.openFilters();
      }
    },

    openFilters: function() {
      const toggle = document.querySelector(this.config.selectors.filterToggle);
      const panel = document.querySelector(this.config.selectors.filterContent);
      if (!toggle || !panel) return;

      this.state.filtersExpanded = true;
      toggle.setAttribute('aria-expanded', 'true');
      panel.hidden = false;

      // Focus the collection search for immediate typing
      const search = panel.querySelector(this.config.selectors.collectionSearch);
      if (search) search.focus();
    },

    closeFilters: function() {
      const toggle = document.querySelector(this.config.selectors.filterToggle);
      const panel = document.querySelector(this.config.selectors.filterContent);
      if (!toggle || !panel) return;

      this.state.filtersExpanded = false;
      toggle.setAttribute('aria-expanded', 'false');
      panel.hidden = true;
    },

    /**
     * Handle collection filter button click
     */
    handleFilterClick: function(event) {
      const button = event.currentTarget;
      const filter = button.getAttribute('data-collection-filter');
      // Read the visible (server-localized) name rather than hardcoding English
      const buttonName = button.querySelector('.filter-item__name');
      const collectionTitle = button.getAttribute('data-collection-title')
        || (buttonName ? buttonName.textContent.trim() : '');

      // Update button states
      document.querySelectorAll(this.config.selectors.filterButton).forEach(btn => {
        btn.classList.remove('filter-list__item--active');
        btn.setAttribute('aria-pressed', 'false');
      });

      button.classList.add('filter-list__item--active');
      button.setAttribute('aria-pressed', 'true');

      // Reflect the selection on the popover trigger
      const filterCount = document.querySelector(this.config.selectors.filterCount);
      if (filterCount) {
        filterCount.textContent = collectionTitle;
      }

      // Update state
      this.state.currentFilter = filter;

      // Filter products
      this.filterProducts(filter);

      // Re-apply search if active
      const productSearch = document.querySelector(this.config.selectors.productSearch);
      if (productSearch && productSearch.value.trim() !== '') {
        this.handleProductSearch({ target: productSearch });
      }

      // Selection made — close the popover
      this.closeFilters();
    },

    /**
     * Filter products by collection
     */
    filterProducts: function(filter) {
      const productRows = document.querySelectorAll(this.config.selectors.productRow);
      let visibleCount = 0;

      productRows.forEach(row => {
        if (filter === 'all') {
          row.style.display = '';
          visibleCount++;
        } else {
          const productCollections = row.getAttribute('data-collections');
          const collectionsArray = productCollections ? productCollections.split(',') : [];

          if (collectionsArray.includes(filter)) {
            row.style.display = '';
            visibleCount++;
          } else {
            row.style.display = 'none';
          }
        }
      });

      // Update the visible product count
      this.updateVisibleProductCount(visibleCount, filter);
    },

    /**
     * Update visible product count in pagination info
     */
    updateVisibleProductCount: function(visibleCount, filter) {
      const paginationInfo = document.querySelector('[data-pagination-info]');
      const visibleCountElement = document.querySelector('[data-visible-count]');

      if (paginationInfo && visibleCountElement) {
        visibleCountElement.textContent = visibleCount;
      }

      this.toggleEmptyState(visibleCount);
    },

    /**
     * Show or hide the empty state when no products match search + filters
     */
    toggleEmptyState: function(visibleCount) {
      const emptyState = document.querySelector(this.config.selectors.emptyState);
      if (!emptyState) return;

      if (visibleCount === 0) {
        emptyState.hidden = false;
        this.updateEmptyStatePagination();
      } else {
        emptyState.hidden = true;
      }
    },

    /**
     * When the catalogue spans more than one server page, the client-side
     * search only sees the current page. Rather than imply "no results
     * anywhere", reveal a hint plus a link to the full store search scoped
     * to the current query.
     */
    updateEmptyStatePagination: function() {
      const list = document.querySelector(this.config.selectors.productsList);
      const totalPages = list ? parseInt(list.getAttribute('data-total-pages'), 10) || 1 : 1;

      const hint = document.querySelector(this.config.selectors.emptyOtherPages);
      const storeLink = document.querySelector(this.config.selectors.emptySearchStore);
      const multiPage = totalPages > 1;

      if (hint) hint.hidden = !multiPage;
      if (storeLink) {
        storeLink.hidden = !multiPage;
        if (multiPage) {
          const search = document.querySelector(this.config.selectors.productSearch);
          const term = search ? search.value.trim() : '';
          storeLink.href = '/search?type=product&q=' + encodeURIComponent(term);
        }
      }
    },

    /**
     * Clear search + filters to start fresh
     */
    resetFilters: function() {
      const productSearch = document.querySelector(this.config.selectors.productSearch);
      if (productSearch) {
        productSearch.value = '';
      }

      // Reset active collection filter back to All Products
      const allFilterButton = document.querySelector(`${this.config.selectors.filterButton}[data-collection-filter="all"]`);
      if (allFilterButton) {
        allFilterButton.click();
      } else {
        this.state.currentFilter = 'all';
        this.filterProducts('all');
      }

      // Ensure product search re-applies after filter reset
      this.handleProductSearch({ target: productSearch || { value: '' } });
    },

    /**
     * Bulk select all visible products (set quantity to 1)
     */
    bulkSelectAll: function() {
      const productRows = document.querySelectorAll(this.config.selectors.productRow);
      let count = 0;

      productRows.forEach(row => {
        // Only select visible rows
        if (row.style.display !== 'none') {
          const pcsInput = row.querySelector('[data-qty-input="pcs"]');
          if (pcsInput && parseInt(pcsInput.value) === 0) {
            pcsInput.value = '1';
            this.updateRowSubtotal(pcsInput);
            count++;
          }
        }
      });

      this.updateSidebar();
      this.scheduleAutosave();
      this.showMessage('success', this.t('selected_n', count));
    },

    /**
     * Clear all quantities
     */
    clearAll: function() {
      const dialog = document.querySelector(this.config.selectors.clearDialog);

      // Prefer the accessible <dialog>; fall back to a native confirm() only
      // where showModal isn't supported.
      if (dialog && typeof dialog.showModal === 'function') {
        dialog.showModal();
        return;
      }

      if (confirm(this.t('confirm_clear'))) {
        this.performClearAll();
      }
    },

    /**
     * Actually reset every quantity to zero.
     */
    performClearAll: function() {
      // Snapshot the full saved order (DOM flushed first) so clear is undoable.
      this.saveQuantities();
      this._clearUndoSnapshot = localStorage.getItem(this.state.storageKey) || null;

      document.querySelectorAll(this.config.selectors.qtyInput).forEach(input => {
        input.value = '0';
        const row = input.closest(this.config.selectors.productRow);
        if (row) {
          const subtotalElement = row.querySelector(this.config.selectors.lineSubtotal);
          if (subtotalElement) {
            subtotalElement.textContent = this.formatMoney(0);
          }
        }
      });

      // Clear storage BEFORE recomputing totals — the totals merge in saved
      // items from other pagination pages, which must not survive a clear.
      this.clearSavedQuantities();
      this.updateSidebar();
      this.showClearedUndo();
    },

    /**
     * Export order to CSV
     */
    exportToCSV: function() {
      const items = this.getSelectedItems();

      if (items.length === 0) {
        this.showMessage('error', this.t('no_items_export'));
        return;
      }

      // Build CSV rows
      let header = 'Product,Variant,SKU,Barcode,Qty (pcs),Qty (karton),Total Pieces';
      if (this.state.isCustomerLoggedIn) {
        header += ',Price,Subtotal';
      }

      let csvContent = header + '\n';

      items.forEach(item => {
        const row = [
          this.escapeCSV(item.productTitle),
          this.escapeCSV(item.variantTitle),
          this.escapeCSV(item.sku),
          this.escapeCSV(item.barcode || ''),
          item.pcsQty,
          item.kartons,
          item.quantity
        ];

        if (this.state.isCustomerLoggedIn) {
          const priceFormatted = this.formatMoney(item.price / 100);
          const subtotal = (item.quantity * item.price) / 100;
          row.push(this.escapeCSV(priceFormatted), this.escapeCSV(this.formatMoney(subtotal)));
        }

        csvContent += row.join(',') + '\n';
      });

      // Download via a Blob URL. A data: URI (the previous approach) is capped
      // by browser URL-length limits and would truncate large orders; a Blob
      // has no such ceiling. Prepend a UTF-8 BOM so Excel reads accents correctly.
      const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `bulk_order_${new Date().getTime()}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      this.showMessage('success', this.t('exported'));
    },

    /**
     * Escape CSV values
     */
    escapeCSV: function(value) {
      if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
        return '"' + value.replace(/"/g, '""') + '"';
      }
      return value;
    },

    /**
     * Save draft order
     */
    saveDraft: function() {
      const items = this.getSelectedItems();

      if (items.length === 0) {
        this.showMessage('error', this.t('no_items_save'));
        return;
      }

      try {
        const draft = {
          items: items,
          timestamp: new Date().toISOString(),
          filter: this.state.currentFilter
        };

        localStorage.setItem(this.state.draftKey, JSON.stringify(draft));
        this.showMessage('success', this.t('draft_saved_n', items.length));
      } catch (e) {
        console.error('Error saving draft:', e);
        this.showMessage('error', this.t('could_not_save'));
      }
    },

    /**
     * Handle quantity button click (+ or -)
     */
    handleQuantityButtonClick: function(event) {
      const button = event.currentTarget;
      const unitType = button.getAttribute('data-qty-change'); // 'pcs' or 'karton'
      const action = button.getAttribute('data-qty-action'); // 'increase' or 'decrease'

      // Find the input in the same parent
      const parent = button.closest('.bulk-order__quantity-wrapper');
      const input = parent.querySelector(`[data-qty-input="${unitType}"]`);

      if (!input) return;

      let currentValue = parseInt(input.value) || 0;
      const step = 1;

      if (action === 'increase') {
        currentValue += step;
      } else if (action === 'decrease' && currentValue > 0) {
        currentValue -= step;
      }

      input.value = currentValue;

      // Update calculations
      this.updateRowSubtotal(input);
      this.updateSidebar();
      this.scheduleAutosave();
    },

    /**
     * Handle direct quantity input
     */
    handleQuantityInput: function(event) {
      const input = event.currentTarget;
      this.updateRowSubtotal(input);
      this.updateSidebar();
      this.scheduleAutosave();
    },

    /**
     * Spreadsheet-style entry: Enter jumps to the same quantity column in
     * the next visible row (Shift+Enter goes up). Arrow keys keep their
     * native number-input increment/decrement behaviour.
     */
    handleQtyKeydown: function(event) {
      if (event.key !== 'Enter') return;
      event.preventDefault();

      const input = event.currentTarget;
      const unitType = input.getAttribute('data-qty-input');
      const currentRow = input.closest(this.config.selectors.productRow);
      if (!currentRow) return;

      const visibleRows = Array.from(
        document.querySelectorAll(this.config.selectors.productRow)
      ).filter(row => row.style.display !== 'none');

      const index = visibleRows.indexOf(currentRow);
      const target = visibleRows[index + (event.shiftKey ? -1 : 1)];
      if (!target) return;

      const nextInput = target.querySelector(`[data-qty-input="${unitType}"]`);
      if (nextInput) {
        nextInput.focus();
        nextInput.select();
      }
    },

    /**
     * Validate quantity input on blur
     */
    validateQuantityInput: function(event) {
      const input = event.currentTarget;
      let value = parseInt(input.value);

      if (isNaN(value) || value < 0) {
        value = 0;
      }

      input.value = value;
      this.updateSidebar();
      this.scheduleAutosave();
    },

    /**
     * Update row subtotal (for logged-in customers)
     */
    updateRowSubtotal: function(input) {
      if (!this.state.isCustomerLoggedIn) return;

      const row = input.closest(this.config.selectors.productRow);
      if (!row) return;

      const pcsInput = row.querySelector('[data-qty-input="pcs"]');
      const kartonInput = row.querySelector('[data-qty-input="karton"]');
      const cartonQty = parseInt(pcsInput?.getAttribute('data-carton-qty')) || 12;
      const priceElement = row.querySelector('[data-variant-price]');
      const subtotalElement = row.querySelector(this.config.selectors.lineSubtotal);

      if (!pcsInput || !kartonInput || !priceElement || !subtotalElement) return;

      const pcsQuantity = parseInt(pcsInput.value) || 0;
      const kartonQuantity = parseInt(kartonInput.value) || 0;
      const totalPcs = pcsQuantity + (kartonQuantity * cartonQty);
      const priceInCents = parseInt(priceElement.getAttribute('data-variant-price')) || 0;

      const subtotal = (totalPcs * priceInCents) / 100;

      // Format as currency
      subtotalElement.textContent = this.formatMoney(subtotal);
    },

    /**
     * Update sidebar with totals and selected items
     */
    updateSidebar: function() {
      const items = this.getSelectedItems();

      // Update counts
      this.state.totalItems = items.length;
      this.state.totalPieces = items.reduce((sum, item) => sum + item.quantity, 0);
      this.state.totalAmount = items.reduce((sum, item) => sum + (item.quantity * item.price) / 100, 0);

      // Update stat displays
      const totalItemsEl = document.querySelector(this.config.selectors.totalItems);
      if (totalItemsEl) {
        totalItemsEl.textContent = this.state.totalItems;
      }

      const totalPiecesEl = document.querySelector(this.config.selectors.totalPieces);
      if (totalPiecesEl) {
        totalPiecesEl.textContent = this.state.totalPieces;
      }

      const summaryTotalEl = document.querySelector(this.config.selectors.summaryTotal);
      if (summaryTotalEl && this.state.isCustomerLoggedIn) {
        summaryTotalEl.textContent = this.formatMoney(this.state.totalAmount);
      }

      // Update selected items preview
      this.updateSelectedItemsPreview(items);

      // Visual emphasis: steppers with a value stand out from untouched ones
      this.updateQtyEmphasis();
    },

    /**
     * Toggle .has-qty on every stepper wrapper so filled controls render at
     * full strength while untouched ones recede (see CSS). classList.toggle
     * with an unchanged value is a no-op, so this stays cheap at 800+ inputs.
     */
    updateQtyEmphasis: function() {
      document.querySelectorAll(this.config.selectors.qtyInput).forEach(input => {
        const wrapper = input.closest('.bulk-order__quantity-wrapper');
        if (wrapper) {
          wrapper.classList.toggle('has-qty', (parseInt(input.value) || 0) > 0);
        }
      });
    },

    /**
     * Update selected items preview in sidebar
     */
    updateSelectedItemsPreview: function(items) {
      const listEl = document.querySelector(this.config.selectors.selectedItemsList);
      if (!listEl) return;

      if (items.length === 0) {
        listEl.innerHTML = '<p class="items-preview__empty">No items selected</p>';
        return;
      }

      // Show max 10 items in preview
      const displayItems = items.slice(0, 10);

      let html = '';
      displayItems.forEach(item => {
        const priceDisplay = this.state.isCustomerLoggedIn
          ? `<span class="preview-item__price">${this.formatMoney((item.quantity * item.price) / 100)}</span>`
          : '';

        html += `
          <div class="preview-item">
            ${item.imageUrl ? `
              <div class="preview-item__image">
                <img src="${item.imageUrl}" alt="${item.productTitle}" width="40" height="40">
              </div>
            ` : ''}
            <div class="preview-item__details">
              <div class="preview-item__name">${item.productTitle}</div>
              <div class="preview-item__qty">${item.quantity} ${this.t('pcs')}</div>
            </div>
            ${priceDisplay}
          </div>
        `;
      });

      if (items.length > 10) {
        html += `<p class="items-preview__more">${this.t('more_n', items.length - 10)}</p>`;
      }

      listEl.innerHTML = html;
    },

    /**
     * Get selected items
     */
    getSelectedItems: function() {
      const items = [];
      const domVariantIds = new Set();

      // Quantities are ORDER state, not view state: rows hidden by the
      // search/filter must still count. (Skipping them silently dropped
      // lines from totals, exports and submission whenever a filter was
      // active at submit time.)
      document.querySelectorAll(this.config.selectors.productRow).forEach(row => {
        const pcsInput = row.querySelector('[data-qty-input="pcs"]');
        const kartonInput = row.querySelector('[data-qty-input="karton"]');
        const variantId = row.getAttribute('data-variant-id');

        if (variantId) domVariantIds.add(variantId);
        if (!pcsInput || !kartonInput) return;

        const pcsQty = parseInt(pcsInput.value) || 0;
        const kartonQty = parseInt(kartonInput.value) || 0;
        const cartonSize = parseInt(pcsInput.getAttribute('data-carton-qty')) || 12;
        const totalPcs = pcsQty + (kartonQty * cartonSize);

        if (totalPcs > 0) {
          // Get product info
          const productTitle = row.querySelector('.bulk-order__product-title')?.textContent.trim() || '';
          const variantTitle = row.querySelector('.bulk-order__variant-title')?.textContent.trim() || '';
          const skuEl = row.querySelector('.bulk-order__variant-sku');
          const sku = (skuEl?.dataset.sku || skuEl?.textContent.replace('SKU: ', '') || '').trim();
          const barcode = row.querySelector('.bulk-order__barcode')?.textContent.trim() || '';
          const price = row.querySelector('[data-variant-price]')?.getAttribute('data-variant-price') || '0';
          const imageUrl = row.querySelector('.bulk-order__product-image img')?.src || '';

          items.push({
            variantId: variantId,
            productTitle: productTitle,
            variantTitle: variantTitle,
            sku: sku,
            barcode: barcode,
            quantity: totalPcs,  // Total combined quantity
            pcsQty: pcsQty,      // Individual pcs for display
            kartons: kartonQty,  // Individual kartons for display
            cartonSize: cartonSize,
            price: parseInt(price),
            imageUrl: imageUrl
          });
        }
      });

      // With a paginated catalogue only the current page's rows exist in the
      // DOM. Append saved items from OTHER pages (their display data was
      // captured into the draft when they were edited), so totals, exports
      // and submission cover the whole order — not just the visible page.
      const saved = this.state.draftRestored ? this.readSavedQuantities() : null;
      if (saved) {
        for (const variantId in saved.quantities) {
          if (domVariantIds.has(variantId)) continue;

          const entry = saved.quantities[variantId];
          const meta = entry && entry.meta;
          if (!meta) continue;

          const pcsQty = parseInt(entry.pcs) || 0;
          const kartonQty = parseInt(entry.karton) || 0;
          const cartonSize = parseInt(meta.cartonSize) || 12;
          const totalPcs = pcsQty + (kartonQty * cartonSize);
          if (totalPcs <= 0) continue;

          items.push({
            variantId: variantId,
            productTitle: meta.title || '',
            variantTitle: meta.variant || '',
            sku: meta.sku || '',
            barcode: meta.barcode || '',
            quantity: totalPcs,
            pcsQty: pcsQty,
            kartons: kartonQty,
            cartonSize: cartonSize,
            price: parseInt(meta.price) || 0,
            imageUrl: meta.image || ''
          });
        }
      }

      return items;
    },

    /**
     * Format money using Shopify's money format
     */
    formatMoney: function(amount) {
      const cents = Math.round(Number(amount || 0) * 100);
      const formatString = this.state.moneyFormat || '${{amount}}';

      const formatWithDelimiters = (price, precision = 2, thousands = ',', decimal = '.') => {
        if (isNaN(price)) return '0.00';

        // Shopify money values are ALWAYS in subunits (1/100), regardless of
        // how many decimals the display format shows — so divide by 100
        // first, THEN apply the precision. Dividing by 10^precision made
        // no-decimals formats (like this store's HUF) come out 100× too big.
        const number = (price / 100).toFixed(precision);
        const parts = number.split('.');
        const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
        const centsValue = parts[1] ? `${decimal}${parts[1]}` : '';

        return dollars + centsValue;
      };

      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const match = formatString.match(placeholderRegex);
      const placeholder = match ? match[1] : 'amount';

      let value;

      switch (placeholder) {
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        case 'amount_with_currency':
          value = formatWithDelimiters(cents);
          break;
        case 'amount_no_decimals_with_currency':
          value = formatWithDelimiters(cents, 0);
          break;
        default:
          value = formatWithDelimiters(cents);
      }

      return formatString.replace(placeholderRegex, value);
    },

    /**
     * Get contact information
     */
    getContactInformation: function() {
      const contactForm = document.querySelector(this.config.selectors.contactForm);
      if (!contactForm) return null;

      return {
        company: contactForm.querySelector('[data-contact-field="company"]')?.value.trim() || '',
        name: contactForm.querySelector('[data-contact-field="name"]')?.value.trim() || '',
        email: contactForm.querySelector('[data-contact-field="email"]')?.value.trim() || '',
        phone: contactForm.querySelector('[data-contact-field="phone"]')?.value.trim() || ''
      };
    },

    /**
     * Validate contact field
     */
    validateContactField: function(event) {
      const field = event.currentTarget;
      const fieldName = field.getAttribute('data-contact-field');
      const value = field.value.trim();
      const errorElement = document.querySelector(`[data-error-for="${fieldName}"]`);

      let errorMessage = '';

      // Required field check
      if (!value) {
        errorMessage = this.t('field_required');
      } else {
        // Email validation
        if (fieldName === 'email') {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            errorMessage = this.t('invalid_email');
          }
        }

        // Phone validation (basic)
        if (fieldName === 'phone') {
          const phoneRegex = /^[\d\s\+\-\(\)]+$/;
          if (!phoneRegex.test(value) || value.length < 10) {
            errorMessage = this.t('invalid_phone');
          }
        }
      }

      // Display error
      if (errorElement) {
        errorElement.textContent = errorMessage;
      }

      if (errorMessage) {
        field.classList.add('error');
        return false;
      } else {
        field.classList.remove('error');
        return true;
      }
    },

    /**
     * Validate all contact fields
     */
    validateAllContactFields: function() {
      const contactFields = document.querySelectorAll(this.config.selectors.contactField);
      let allValid = true;

      contactFields.forEach(field => {
        const isValid = this.validateContactField({ currentTarget: field });
        if (!isValid) allValid = false;
      });

      return allValid;
    },

    /**
     * Handle form submission
     */
    handleSubmit: function(event) {
      const button = event.currentTarget;
      const action = button.getAttribute('data-action');

      // Get selected items
      const items = this.getSelectedItems();

      if (items.length === 0) {
        this.showMessage('error', this.t('select_one'));
        return;
      }

      if (action === 'add-to-cart') {
        this.handleAddToCart(items, button);
      } else if (action === 'request-quote') {
        // Contact details are collected at the submit step: open the quote
        // dialog. Fall back to the inline flow if <dialog> is unsupported.
        const quoteDialog = document.querySelector(this.config.selectors.quoteDialog);
        if (quoteDialog && typeof quoteDialog.showModal === 'function') {
          quoteDialog.showModal();
        } else {
          this.handleRequestQuote(items, button);
        }
      }
    },

    /**
     * Send from inside the quote dialog: validate contact fields, then submit.
     */
    handleQuoteSend: function(button) {
      const items = this.getSelectedItems();

      if (items.length === 0) {
        const quoteDialog = document.querySelector(this.config.selectors.quoteDialog);
        if (quoteDialog && quoteDialog.open) quoteDialog.close();
        this.showMessage('error', this.t('select_one'));
        return;
      }

      this.handleRequestQuote(items, button);
    },

    /**
     * Handle add to cart (logged-in customers)
     */
    handleAddToCart: function(items, button) {
      // Show loading state
      this.setButtonLoading(button, true);

      // Prepare cart items for Shopify
      const cartItems = items.map(item => ({
        id: item.variantId,
        quantity: item.quantity
      }));

      // Add to cart using Shopify Ajax API
      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items: cartItems })
      })
      .then(response => response.json().then(data => ({ ok: response.ok, data: data })))
      .then(result => {
        // Shopify returns a 422 with { status, message, description } when a line
        // can't be added (out of stock, quantity rules, sold-out variant, …).
        // Without this check a rejected add was being reported as success.
        if (!result.ok) {
          const detail = result.data && (result.data.description || result.data.message);
          throw new Error(detail || 'cart_add_failed');
        }

        this.setButtonLoading(button, false);
        this.state.orderCompleted = true;
        this.clearSavedQuantities();
        this.showMessage('success', this.t('added_n', items.length));

        // Redirect to cart after a short delay
        setTimeout(() => {
          window.location.href = '/cart';
        }, 1500);
      })
      .catch(error => {
        this.setButtonLoading(button, false);
        console.error('Error adding to cart:', error);
        // Surface Shopify's own reason when we have one; otherwise the generic message.
        const detail = error && error.message && error.message !== 'cart_add_failed'
          ? error.message
          : this.t('cart_error');
        this.showMessage('error', detail);
      });
    },

    /**
     * Handle request quote (guest users)
     */
    handleRequestQuote: function(items, button) {
      // Validate contact information
      if (!this.validateAllContactFields()) {
        this.showMessage('error', this.t('fill_contact'));
        return;
      }

      const contactInfo = this.getContactInformation();

      // Show loading state
      this.setButtonLoading(button, true);

      // Prepare email data
      const emailData = {
        contact: contactInfo,
        items: items,
        timestamp: new Date().toISOString()
      };

      // Send quote request via Shopify contact form
      this.sendQuoteRequest(emailData, button);
    },

    /**
     * Send quote request
     */
    sendQuoteRequest: function(emailData, button) {
      // Build the email body
      let emailBody = `NEW BULK ORDER QUOTE REQUEST\n\n`;
      emailBody += `Contact Information:\n`;
      emailBody += `Company: ${emailData.contact.company}\n`;
      emailBody += `Name: ${emailData.contact.name}\n`;
      emailBody += `Email: ${emailData.contact.email}\n`;
      emailBody += `Phone: ${emailData.contact.phone}\n\n`;
      emailBody += `Order Details:\n`;
      emailBody += `Requested on: ${new Date(emailData.timestamp).toLocaleString()}\n\n`;

      emailData.items.forEach((item, index) => {
        emailBody += `${index + 1}. ${item.productTitle} - ${item.variantTitle}\n`;
        if (item.sku) emailBody += `   SKU: ${item.sku}\n`;
        if (item.barcode) emailBody += `   Barcode: ${item.barcode}\n`;
        emailBody += `   Quantity: ${item.quantity} pcs (${item.pcsQty} pcs + ${item.kartons} karton × ${item.cartonSize})\n\n`;
      });

      // Use Shopify contact form endpoint
      const formData = new FormData();
      formData.append('contact[email]', emailData.contact.email);
      formData.append('contact[name]', emailData.contact.name);
      formData.append('contact[company]', emailData.contact.company);
      formData.append('contact[phone]', emailData.contact.phone);
      formData.append('contact[body]', emailBody);
      formData.append('form_type', 'contact');

      fetch('/contact', {
        method: 'POST',
        body: formData
      })
      .then(response => {
        this.setButtonLoading(button, false);

        if (response.ok) {
          // Close the quote dialog before showing the confirmation
          const quoteDialog = document.querySelector(this.config.selectors.quoteDialog);
          if (quoteDialog && quoteDialog.open) quoteDialog.close();

          this.state.orderCompleted = true;
          this.clearSavedQuantities();
          this.showMessage('success', this.t('quote_sent'));

          // Reset form after successful submission
          setTimeout(() => {
            this.resetForm();
          }, 2000);
        } else {
          throw new Error('Form submission failed');
        }
      })
      .catch(error => {
        this.setButtonLoading(button, false);
        console.error('Error sending quote:', error);
        this.showMessage('error', this.t('quote_error'));
      });
    },

    /**
     * Set button loading state
     */
    setButtonLoading: function(button, isLoading) {
      button.classList.toggle('loading', isLoading);
      button.disabled = isLoading;

      // Loaders are hidden via the `hidden` attribute in the markup;
      // clear any inline display left by older markup as well.
      const loader = button.querySelector('.btn__loader');
      if (loader) {
        loader.hidden = !isLoading;
        loader.style.display = isLoading ? 'inline-flex' : 'none';
      }
    },

    /**
     * Show message
     */
    showMessage: function(type, message) {
      const messagesContainer = document.querySelector(this.config.selectors.messages);
      if (!messagesContainer) return;

      messagesContainer.style.display = 'block';

      const messageElement = document.createElement('div');
      messageElement.className = `bulk-order__message bulk-order__message--${type}`;
      messageElement.textContent = message;

      messagesContainer.innerHTML = '';
      messagesContainer.appendChild(messageElement);

      // Scroll to message
      messageElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

      // Auto-hide success messages after 5 seconds: fade out, then remove
      if (type === 'success') {
        setTimeout(() => {
          messageElement.classList.add('is-leaving');
          setTimeout(() => {
            messageElement.remove();
            if (messagesContainer.children.length === 0) {
              messagesContainer.style.display = 'none';
            }
          }, 200);
        }, 5000);
      }
    },

    /**
     * Reset form
     */
    resetForm: function() {
      // Reset all quantity inputs
      document.querySelectorAll(this.config.selectors.qtyInput).forEach(input => {
        input.value = '0';
      });

      // Reset contact form if guest
      if (!this.state.isCustomerLoggedIn) {
        const contactForm = document.querySelector(this.config.selectors.contactForm);
        if (contactForm) {
          contactForm.reset();
          // Clear error messages
          document.querySelectorAll('.bulk-order-contact__error').forEach(error => {
            error.textContent = '';
          });
          // Remove error classes
          document.querySelectorAll('.bulk-order-contact__input').forEach(input => {
            input.classList.remove('error');
          });
        }
      }

      // Clear storage BEFORE recomputing totals — the totals merge in saved
      // items from other pagination pages, which must not survive a reset.
      this.clearSavedQuantities();
      this.updateSidebar();

      // Reset filter to "All"
      const allButton = document.querySelector('[data-collection-filter="all"]');
      if (allButton) {
        allButton.click();
      }

      // Clear search
      const productSearch = document.querySelector(this.config.selectors.productSearch);
      if (productSearch) {
        productSearch.value = '';
      }
    },

    /**
     * Read the auto-saved order from localStorage.
     * Handles both the current format ({ v: 2, updatedAt, quantities })
     * and the legacy plain quantity map. Returns null when nothing usable.
     */
    readSavedQuantities: function() {
      try {
        const raw = localStorage.getItem(this.state.storageKey);
        if (!raw) return null;

        const parsed = JSON.parse(raw);
        const quantities = (parsed && parsed.v === 2) ? parsed.quantities : parsed;
        if (!quantities || typeof quantities !== 'object') return null;

        // Count variants with any quantity > 0
        let count = 0;
        for (const variantId in quantities) {
          const entry = quantities[variantId];
          if (entry && (parseInt(entry.pcs) > 0 || parseInt(entry.karton) > 0)) {
            count++;
          }
        }
        if (count === 0) return null;

        return {
          quantities: quantities,
          count: count,
          updatedAt: (parsed && parsed.updatedAt) || null
        };
      } catch (e) {
        console.warn('Could not read saved quantities:', e);
        return null;
      }
    },

    /**
     * Auto-save quantities to localStorage.
     * MERGES with what's already saved: with a paginated catalogue only the
     * current page's rows exist in the DOM, so rebuilding from scratch would
     * silently wipe quantities entered on other pages. Merging is gated on
     * `draftRestored`: if the user dismissed the resume dialog and starts a
     * NEW order, the old draft is replaced rather than blended in.
     * Each line also captures its display data (title, price, carton size…)
     * so off-page items can appear in totals, previews and submissions.
     */
    saveQuantities: function() {
      const saved = this.state.draftRestored ? this.readSavedQuantities() : null;
      const quantities = saved ? saved.quantities : {};

      document.querySelectorAll(this.config.selectors.productRow).forEach(row => {
        const variantId = row.getAttribute('data-variant-id');
        if (!variantId) return;

        const pcs = parseInt(row.querySelector('[data-qty-input="pcs"]')?.value) || 0;
        const karton = parseInt(row.querySelector('[data-qty-input="karton"]')?.value) || 0;

        if (pcs <= 0 && karton <= 0) {
          delete quantities[variantId];
          return;
        }

        const skuEl = row.querySelector('.bulk-order__variant-sku');
        const pcsInput = row.querySelector('[data-qty-input="pcs"]');

        const entry = {};
        if (pcs > 0) entry.pcs = String(pcs);
        if (karton > 0) entry.karton = String(karton);
        entry.meta = {
          title: row.querySelector('.bulk-order__product-title')?.textContent.trim() || '',
          variant: row.querySelector('.bulk-order__variant-title')?.textContent.trim() || '',
          sku: (skuEl?.dataset.sku || '').trim(),
          barcode: row.querySelector('.bulk-order__barcode')?.textContent.trim() || '',
          price: row.querySelector('[data-variant-price]')?.getAttribute('data-variant-price') || '0',
          image: row.querySelector('.bulk-order__product-image img')?.src || '',
          cartonSize: pcsInput?.getAttribute('data-carton-qty') || '12'
        };

        quantities[variantId] = entry;
      });

      try {
        if (Object.keys(quantities).length === 0) {
          localStorage.removeItem(this.state.storageKey);
        } else {
          localStorage.setItem(this.state.storageKey, JSON.stringify({
            v: 2,
            updatedAt: new Date().toISOString(),
            quantities: quantities
          }));
        }
      } catch (e) {
        console.warn('Could not save quantities to localStorage:', e);
      }

      // From here on this draft is the live order — subsequent saves merge
      this.state.draftRestored = true;
    },

    /**
     * Debounced auto-save — called on every quantity change (stepper clicks
     * included, which never fire the input's native `change` event).
     */
    scheduleAutosave: function() {
      // A fresh user edit means a new order is in progress again
      this.state.orderCompleted = false;

      if (this.state.autosaveTimeout) {
        clearTimeout(this.state.autosaveTimeout);
      }
      this.state.autosaveTimeout = setTimeout(() => {
        this.saveQuantities();
      }, 400);
    },

    /**
     * Apply the saved order to the form (current page's rows).
     */
    restoreQuantities: function() {
      const saved = this.readSavedQuantities();
      if (!saved) return;

      this.state.draftRestored = true;

      document.querySelectorAll(this.config.selectors.qtyInput).forEach(input => {
        const variantId = input.getAttribute('data-variant-id');
        const unitType = input.getAttribute('data-qty-input');

        if (variantId && saved.quantities[variantId] && saved.quantities[variantId][unitType]) {
          input.value = saved.quantities[variantId][unitType];
        }
      });

      // Update all row subtotals and sidebar
      document.querySelectorAll(this.config.selectors.qtyInput).forEach(input => {
        if (parseInt(input.value) > 0) {
          this.updateRowSubtotal(input);
        }
      });
      this.updateSidebar();
    },

    /**
     * On load: when an unfinished order exists, ask before restoring it.
     * Continue → restore; start fresh → discard the saved order.
     * Dismissing the dialog (Esc / backdrop) keeps the saved order for later
     * without applying it. Falls back to silent restore without <dialog>.
     */
    maybeOfferResume: function() {
      const saved = this.readSavedQuantities();
      if (!saved) return;

      const dialog = document.querySelector(this.config.selectors.resumeDialog);
      if (!dialog || typeof dialog.showModal !== 'function') {
        this.restoreQuantities();
        return;
      }

      const body = dialog.querySelector(this.config.selectors.resumeBody);
      if (body) {
        body.textContent = this.t('resume_n', saved.count);
      }

      // Returning mid-session (e.g. pagination) shouldn't re-ask: if the URL
      // carries a page param, the user is actively working — just restore.
      if (window.location.search.includes('page=')) {
        this.restoreQuantities();
        return;
      }

      dialog.showModal();
    },

    /**
     * Clear saved quantities from localStorage
     */
    clearSavedQuantities: function() {
      // Cancel any pending debounced save so it can't resurrect the draft
      // right after a successful submit or an explicit discard.
      if (this.state.autosaveTimeout) {
        clearTimeout(this.state.autosaveTimeout);
        this.state.autosaveTimeout = null;
      }

      try {
        localStorage.removeItem(this.state.storageKey);
      } catch (e) {
        console.warn('Could not clear saved quantities:', e);
      }
    },

    /**
     * Bind pagination events to save state
     */
    bindPaginationEvents: function() {
      // Save quantities whenever an input changes
      document.querySelectorAll(this.config.selectors.qtyInput).forEach(input => {
        input.addEventListener('change', () => {
          this.saveQuantities();
        });
      });

      // Save quantities before navigating to another page
      document.querySelectorAll('.pagination-link').forEach(link => {
        link.addEventListener('click', () => {
          this.saveQuantities();
        });
      });

      // Flush on tab close / navigation so an interrupted session never loses
      // the last change (the debounced save may still be pending). Skipped
      // once the order was successfully submitted.
      window.addEventListener('pagehide', () => {
        if (!this.state.orderCompleted) {
          this.saveQuantities();
        }
      });

      // Scroll to top when pagination loads (smooth scroll)
      const paginationSection = document.querySelector(this.config.selectors.section);
      if (paginationSection && window.location.search.includes('page=')) {
        setTimeout(() => {
          paginationSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    },

    /* =========================================================================
       Reorder (partners): reload a previous order's lines into the pad.
       Lines are Liquid-rendered JSON on the dialog buttons; quantities merge
       into the saved order via the same storage path drafts use, so off-page
       lines keep counting in totals/exports/submission (order-state invariant).
       ========================================================================= */

    bindReorder: function() {
      const openBtn = document.querySelector(this.config.selectors.reorderOpen);
      const dialog = document.querySelector(this.config.selectors.reorderDialog);
      if (!openBtn || !dialog || typeof dialog.showModal !== 'function') return;

      openBtn.addEventListener('click', () => dialog.showModal());
      dialog.querySelector('[data-reorder-cancel]')?.addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.close(); });

      dialog.querySelectorAll('[data-reorder-load]').forEach(btn => {
        btn.addEventListener('click', () => {
          let lines;
          try { lines = JSON.parse(btn.getAttribute('data-lines') || '[]'); }
          catch (e) { this.showMessage('error', this.t('catalog_error')); return; }
          const applied = this.applyLines(lines);
          dialog.close();
          this.showMessage('success', this.t('reorder_loaded_n', applied));
        });
      });
    },

    /**
     * Merge external lines ({id, qty, title, variant, sku, price, image}) into
     * the saved order, then re-render. Quantities ADD to existing pcs.
     * Returns the number of lines applied.
     */
    applyLines: function(lines) {
      if (!Array.isArray(lines) || lines.length === 0) return 0;
      // Same gate as saveQuantities: a draft the user declined to resume must
      // not silently blend into the new order once draftRestored flips on.
      const saved = this.state.draftRestored ? this.readSavedQuantities() : null;
      const quantities = saved ? saved.quantities : {};
      let applied = 0;

      lines.forEach(l => {
        const qty = parseInt(l && l.qty) || 0;
        if (!l || !l.id || qty <= 0) return;
        const prev = quantities[l.id] || {};
        const entry = { pcs: String((parseInt(prev.pcs) || 0) + qty) };
        if (prev.karton) entry.karton = prev.karton;
        entry.meta = prev.meta || {
          title: l.title || '',
          variant: l.variant || '',
          sku: l.sku || '',
          barcode: l.barcode || '',
          price: String(l.price || '0'),
          image: l.image || '',
          cartonSize: String(l.cartonSize || '12')
        };
        quantities[l.id] = entry;
        applied++;
      });

      if (applied === 0) return 0;
      try {
        localStorage.setItem(this.state.storageKey, JSON.stringify({
          v: 2, updatedAt: new Date().toISOString(), quantities: quantities
        }));
      } catch (e) { console.warn('Could not persist lines:', e); return 0; }

      this.state.draftRestored = true;
      this.state.orderCompleted = false;
      this.restoreQuantities();
      this.updateSidebar();
      return applied;
    },

    /* =========================================================================
       Cross-catalog quick search: the local filter only sees the current
       pagination page, so matching products from the REST of the catalog are
       fetched via Shopify's Predictive Search API and offered as addable
       lines (added through applyLines → same order-state machinery).
       ========================================================================= */

    updateCatalogResults: function(term) {
      const panel = document.querySelector(this.config.selectors.catalogResults);
      if (!panel) return;
      if (!term || term.length < 2) { panel.hidden = true; panel.innerHTML = ''; return; }

      if (this.state.catalogAbort) this.state.catalogAbort.abort();
      const ctrl = new AbortController();
      this.state.catalogAbort = ctrl;
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

      fetch(root + 'search/suggest.json?q=' + encodeURIComponent(term) +
            '&resources[type]=product&resources[limit]=8&resources[options][fields]=title,product_type,variants.title,variants.sku,variants.barcode',
            { signal: ctrl.signal })
        .then(r => r.json())
        .then(data => {
          const products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
          const onPage = new Set(
            Array.from(document.querySelectorAll(this.config.selectors.productRow))
              .map(r => r.getAttribute('data-product-handle')).filter(Boolean)
          );
          this.renderCatalogResults(products.filter(p => !onPage.has(p.handle)), panel);
        })
        .catch(() => {});
    },

    renderCatalogResults: function(products, panel) {
      panel.innerHTML = '';
      if (!products.length) { panel.hidden = true; return; }

      // The empty state's "quick search only covers this page" hint is
      // superseded when the cross-catalog matches are right below it.
      const hint = document.querySelector(this.config.selectors.emptyOtherPages);
      const storeLink = document.querySelector(this.config.selectors.emptySearchStore);
      if (hint) hint.hidden = true;
      if (storeLink) storeLink.hidden = true;

      const heading = document.createElement('h4');
      heading.className = 'bulk-order__catalog-heading';
      heading.textContent = this.t('catalog_heading');
      panel.appendChild(heading);

      const list = document.createElement('ul');
      list.className = 'bulk-order__catalog-list';

      products.forEach(p => {
        const li = document.createElement('li');
        li.className = 'bulk-order__catalog-item';

        if (p.image) {
          const img = document.createElement('img');
          img.src = p.image; img.alt = ''; img.width = 40; img.height = 40; img.loading = 'lazy';
          li.appendChild(img);
        }

        const title = document.createElement('span');
        title.className = 'bulk-order__catalog-title';
        title.textContent = p.title;
        li.appendChild(title);

        const qty = document.createElement('input');
        qty.type = 'number'; qty.min = '0'; qty.value = '12'; qty.inputMode = 'numeric';
        qty.className = 'bulk-order__catalog-qty';
        qty.setAttribute('aria-label', this.t('pcs'));
        li.appendChild(qty);

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'btn-action btn-action--primary bulk-order__catalog-add';
        btn.textContent = this.t('catalog_add');
        btn.addEventListener('click', () => this.addFromCatalog(p.handle, qty, btn, li));
        li.appendChild(btn);

        list.appendChild(li);
      });

      panel.appendChild(list);
      panel.hidden = false;
    },

    addFromCatalog: function(handle, qtyInput, btn, li) {
      const qty = parseInt(qtyInput.value) || 0;
      if (qty <= 0) { qtyInput.focus(); return; }
      btn.disabled = true;
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

      fetch(root + 'products/' + handle + '.js')
        .then(r => r.json())
        .then(product => {
          const variants = (product && product.variants) || [];
          if (!variants.length) throw new Error('no variants');

          if (variants.length === 1) {
            const v = variants[0];
            this.applyLines([{
              id: String(v.id), qty: qty, title: product.title,
              variant: v.title === 'Default Title' ? '' : v.title,
              sku: v.sku || '', barcode: v.barcode || '', price: String(v.price || 0),
              image: (product.featured_image || ''),
            }]);
            btn.textContent = this.t('catalog_added');
            setTimeout(() => li.remove(), 1200);
            return;
          }

          // Multi-variant: swap the row into a variant chooser
          li.querySelectorAll('.bulk-order__catalog-qty, .bulk-order__catalog-add').forEach(el => el.remove());
          const sub = document.createElement('ul');
          sub.className = 'bulk-order__catalog-variants';
          variants.forEach(v => {
            const vli = document.createElement('li');
            const label = document.createElement('span');
            label.textContent = v.title + (v.sku ? ' · ' + v.sku : '');
            vli.appendChild(label);
            const vqty = qtyInput.cloneNode(); vqty.value = String(qty);
            vli.appendChild(vqty);
            const vbtn = document.createElement('button');
            vbtn.type = 'button'; vbtn.className = 'btn-action btn-action--primary bulk-order__catalog-add';
            vbtn.textContent = this.t('catalog_add');
            vbtn.addEventListener('click', () => {
              const q = parseInt(vqty.value) || 0;
              if (q <= 0) { vqty.focus(); return; }
              this.applyLines([{
                id: String(v.id), qty: q, title: product.title,
                variant: v.title, sku: v.sku || '', barcode: v.barcode || '',
                price: String(v.price || 0),
                image: (product.featured_image || ''),
              }]);
              vbtn.textContent = this.t('catalog_added');
              vbtn.disabled = true;
            });
            vli.appendChild(vbtn);
            sub.appendChild(vli);
          });
          li.appendChild(sub);
        })
        .catch(() => {
          btn.disabled = false;
          this.showMessage('error', this.t('catalog_error'));
        });
    },

    /* =========================================================================
       Undo for clear-all: the snapshot taken in performClearAll is restored
       through the normal storage → restore path.
       ========================================================================= */

    showClearedUndo: function() {
      const container = document.querySelector(this.config.selectors.messages);
      if (!container) return;
      container.style.display = 'block';

      const el = document.createElement('div');
      el.className = 'bulk-order__message bulk-order__message--success';
      const span = document.createElement('span');
      span.textContent = this.t('all_cleared') + ' ';
      el.appendChild(span);

      if (this._clearUndoSnapshot) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'bulk-order__undo-btn';
        btn.textContent = this.t('undo');
        btn.addEventListener('click', () => { this.undoClearAll(); el.remove(); });
        el.appendChild(btn);
      }

      container.innerHTML = '';
      container.appendChild(el);
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      setTimeout(() => {
        el.classList.add('is-leaving');
        setTimeout(() => el.remove(), 400);
      }, 10000);
    },

    undoClearAll: function() {
      if (!this._clearUndoSnapshot) return;
      try { localStorage.setItem(this.state.storageKey, this._clearUndoSnapshot); }
      catch (e) { return; }
      this._clearUndoSnapshot = null;
      this.state.draftRestored = true;
      this.restoreQuantities();
      this.updateSidebar();
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      BulkOrderForm.init();
    });
  } else {
    BulkOrderForm.init();
  }

  // Export to window for debugging
  window.BulkOrderForm = BulkOrderForm;

})();

/**
 * Image quick view — hover magnifier (fine pointers) + click/tap lightbox.
 * Thumbnails opt in via [data-image-zoom] (see bulk-order-product-row.liquid);
 * the no-image logo placeholder never gets the attribute.
 * NOTE: these document-level listeners register BEFORE BulkOrderForm's (which
 * bind at init), so the Escape handler uses stopImmediatePropagation to keep
 * the section's own Esc handling from also firing while the lightbox is open.
 */
(function() {
  'use strict';

  const SEL = '[data-image-zoom]';
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  const POPOVER_SIZE = 340; // keep in sync with .bulk-order__image-popover CSS
  let popover = null;
  let lightbox = null;
  let lastFocus = null;

  function t(key, fallback) {
    const bo = window.BulkOrderForm;
    return (bo && bo.state && bo.state.strings && bo.state.strings[key]) || fallback;
  }

  function rowCaption(thumb) {
    const row = thumb.closest('[data-product-row]');
    if (!row) return '';
    const title = row.querySelector('.bulk-order__product-title');
    const variant = row.querySelector('.bulk-order__variant-title');
    const parts = [];
    if (title && title.textContent.trim()) parts.push(title.textContent.trim());
    if (variant && variant.textContent.trim()) parts.push(variant.textContent.trim());
    return parts.join(' — ');
  }

  /* ---- Hover magnifier popover ---- */
  function showPopover(thumb) {
    if (!popover) {
      popover = document.createElement('div');
      popover.className = 'bulk-order__image-popover';
      popover.setAttribute('aria-hidden', 'true');
      popover.appendChild(document.createElement('img'));
      document.body.appendChild(popover);
    }
    const img = popover.querySelector('img');
    const src = thumb.getAttribute('data-image-zoom');
    if (img.getAttribute('src') !== src) {
      img.src = src;
      img.alt = '';
    }
    const r = thumb.getBoundingClientRect();
    const margin = 12;
    let left = r.right + margin;
    if (left + POPOVER_SIZE > window.innerWidth - margin) {
      left = Math.max(margin, r.left - margin - POPOVER_SIZE);
    }
    let top = r.top + r.height / 2 - POPOVER_SIZE / 2;
    top = Math.min(Math.max(margin, top), window.innerHeight - POPOVER_SIZE - margin);
    popover.style.left = left + 'px';
    popover.style.top = top + 'px';
    popover.classList.add('is-visible');
  }

  function hidePopover() {
    if (popover) popover.classList.remove('is-visible');
  }

  /* ---- Lightbox ---- */
  function openLightbox(thumb) {
    closeLightbox();
    lastFocus = thumb;
    const caption = rowCaption(thumb);

    lightbox = document.createElement('div');
    lightbox.className = 'bulk-order__image-lightbox';
    lightbox.setAttribute('role', 'dialog');
    lightbox.setAttribute('aria-modal', 'true');
    lightbox.setAttribute('aria-label', caption || t('image_zoom', 'Kép nagyítása'));

    const fig = document.createElement('figure');
    fig.className = 'bulk-order__lightbox-figure';

    const img = document.createElement('img');
    const src = thumb.getAttribute('data-image-zoom');
    const hi = thumb.getAttribute('data-image-zoom-hi');
    img.src = src;
    if (hi) img.srcset = src + ' 1x, ' + hi + ' 2x';
    img.alt = caption;
    fig.appendChild(img);

    if (caption) {
      const cap = document.createElement('figcaption');
      cap.className = 'bulk-order__lightbox-caption';
      cap.textContent = caption;
      fig.appendChild(cap);
    }

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'bulk-order__lightbox-close';
    close.setAttribute('aria-label', t('image_close', 'Bezárás'));
    close.innerHTML = '&times;';
    close.addEventListener('click', closeLightbox);

    lightbox.appendChild(fig);
    lightbox.appendChild(close);
    lightbox.addEventListener('click', function(e) {
      if (e.target === lightbox) closeLightbox();
    });

    document.body.appendChild(lightbox);
    document.body.classList.add('bulk-order-lightbox-open');
    close.focus();
  }

  function closeLightbox() {
    if (!lightbox) return;
    lightbox.remove();
    lightbox = null;
    document.body.classList.remove('bulk-order-lightbox-open');
    if (lastFocus) {
      try { lastFocus.focus(); } catch (e) { /* detached row */ }
      lastFocus = null;
    }
  }

  /* ---- Delegated events ---- */
  document.addEventListener('click', function(e) {
    const thumb = e.target.closest && e.target.closest(SEL);
    if (!thumb) return;
    e.preventDefault();
    hidePopover();
    openLightbox(thumb);
  });

  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape' && lightbox) {
      e.stopImmediatePropagation();
      closeLightbox();
      return;
    }
    if ((e.key === 'Enter' || e.key === ' ') && e.target.closest && e.target.closest(SEL)) {
      e.preventDefault();
      openLightbox(e.target.closest(SEL));
    }
  });

  document.addEventListener('mouseover', function(e) {
    if (!canHover.matches || lightbox) return;
    const thumb = e.target.closest && e.target.closest(SEL);
    if (thumb) showPopover(thumb);
  });

  document.addEventListener('mouseout', function(e) {
    const thumb = e.target.closest && e.target.closest(SEL);
    if (thumb && !(e.relatedTarget && thumb.contains(e.relatedTarget))) hidePopover();
  });

  window.addEventListener('scroll', hidePopover, { passive: true });
})();
